import { useMutation } from '@apollo/client';
import { MutationBaseOptions } from '@apollo/client/core/watchQueryOptions';
import {
  AnalyticIdentifier,
  OrganizationInvitationError,
  OrganizationInvitationErrorSchema,
  createError,
  getExtendedErrors,
} from '@common/neo.tax';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import * as Sentry from '@sentry/react';
import {
  Button,
  ModalActions,
  ModalHeader,
  ControlledInput,
  ControlledRadioGroup,
  GeneralFormError,
  Stack,
  colors,
  Modal,
} from '@ui-components/neo.tax';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  ADD_ORGANIZATION_INVITATION,
  GET_COMPANIES,
  GetCompaniesForUserQueryVariables,
  GET_USER,
  GetUserQueryVariables,
  REPLACE_USER_ACCESS_IN_ORGANIZATION,
  AddOrganizationInvitationMutation,
  AddOrganizationInvitationMutationVariables,
  UpdateUserAccessInOrganizationMutation,
  UpdateUserAccessInOrganizationMutationVariables,
} from '@neo.tax/shared/graphql';
import { useCurrentUser } from '@neo.tax/shared/hooks';
import { useAnalyticId } from '@neo.tax/shared/state';
import { OrganizationMemberItemScope, OrganizationMemberTableItemMember } from '@neo.tax/shared/types';
import { validateEmail } from '@neo.tax/shared/utils';

import { MemberScopeTable } from '../member-scope-table';
import { MemberTableContainer, StyledMembersIcon, StyledTypography } from './edit-organization-member-modal.styled';
import {
  organizationMemberFormSchema,
  OrganizationMemberFormType,
  convertFormStateToOrganizationInvitationInput,
  makeDefaultFormValues,
} from './edit-organization-member-modal.utils';

const SCOPE_OPTIONS = [
  {
    id: OrganizationMemberItemScope.ADMIN,
    value: OrganizationMemberItemScope.ADMIN,
    label: 'Admin',
    description: 'View/edit all data and manage users',
  },
  {
    id: OrganizationMemberItemScope.MEMBER,
    value: OrganizationMemberItemScope.MEMBER,
    label: 'Member',
    description: 'View/edit all data for selected companies',
  },
  {
    id: OrganizationMemberItemScope.NON_FINANCIAL,
    value: OrganizationMemberItemScope.NON_FINANCIAL,
    label: 'Non-Financial',
    description: 'View/edit non-financial data',
  },
];

// Shown only when inviting a new member. Editing an accepted member doesn't regenerate the
// invitation, so TTL doesn't apply. Values are string because ControlledRadioGroup wires
// raw string values through react-hook-form; we coerce back to `7 | 30` in the schema.
const EXPIRATION_OPTIONS = [
  { id: 'expires-7', value: '7', label: '7 days' },
  { id: 'expires-30', value: '30', label: '30 days' },
];

const organizationInvitationRefetchQueries = (userId: string): MutationBaseOptions['refetchQueries'] => [
  {
    query: GET_USER,
    variables: {
      userId,
    } satisfies GetUserQueryVariables,
  },
  {
    query: GET_COMPANIES,
    variables: {
      userId,
    } satisfies GetCompaniesForUserQueryVariables,
  },
];

const buttonTitle = (isNewMember: boolean, loading: boolean): string => {
  if (isNewMember) {
    return loading ? 'Sending Invitation...' : 'Invite New Member';
  }
  return loading ? 'Updating Member...' : 'Update Member';
};

type EditOrganizationMemberModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  organizationId: string;
  member?: OrganizationMemberTableItemMember;
};

export const EditOrganizationMemberModal = ({
  isOpen,
  member,
  organizationId,
  setIsOpen,
}: EditOrganizationMemberModalProps): JSX.Element => {
  const { user } = useCurrentUser();
  if (!user) {
    throw createError('MissingCurrentUserError', 'User not found when trying to invite a new company team member');
  }

  const isNewMember = !member;

  const analyticIdOption = { postfix: isNewMember ? 'new' : 'edit' };
  const emailInputId = useAnalyticId(AnalyticIdentifier.RTC_INVITE_MEMBER_EMAIL_INPUT, analyticIdOption);
  const inviteNewMemberId = useAnalyticId(AnalyticIdentifier.RTC_ACCOUNTANT_INVITE_NEW_MEMBER, analyticIdOption);
  const roleInputId = useAnalyticId(AnalyticIdentifier.RTC_INVITE_MEMBER_ROLE_INPUT, analyticIdOption);
  const [errorMessage, setErrorMessage] = useState<string>();

  const { control, formState, handleSubmit, reset, setValue, watch } = useForm<OrganizationMemberFormType>({
    mode: 'onChange',
    defaultValues: makeDefaultFormValues(member),
    resolver: standardSchemaResolver(organizationMemberFormSchema),
  });

  const [addOrganizationInvitation, { loading: addOrganizationInvitationLoading }] = useMutation<
    AddOrganizationInvitationMutation,
    AddOrganizationInvitationMutationVariables
  >(ADD_ORGANIZATION_INVITATION, {
    refetchQueries: organizationInvitationRefetchQueries(user.id),
    awaitRefetchQueries: true,
  });

  const [replaceUserAccessInOrganization, { loading: replaceUserAccessInOrganizationLoading }] = useMutation<
    UpdateUserAccessInOrganizationMutation,
    UpdateUserAccessInOrganizationMutationVariables
  >(REPLACE_USER_ACCESS_IN_ORGANIZATION, {
    refetchQueries: organizationInvitationRefetchQueries(user.id),
    awaitRefetchQueries: true,
  });

  const createNewInvitation = async (values: OrganizationMemberFormType): Promise<void> => {
    await addOrganizationInvitation({
      variables: {
        organizationId,
        addOrganizationInvitationInput: convertFormStateToOrganizationInvitationInput(values),
      },
    });
  };

  const updateCompanyMember = async (memberId: string, values: OrganizationMemberFormType): Promise<void> => {
    const invitationInput = convertFormStateToOrganizationInvitationInput(values);
    await replaceUserAccessInOrganization({
      variables: {
        organizationId,
        userId: memberId,
        replaceUserAccessInOrganizationInput: {
          organizationScope: invitationInput.organizationScope,
          companyIds: invitationInput.companyIds,
          companyScope: invitationInput.companyScope,
        },
      },
    });
  };

  const onSubmit = async (values: OrganizationMemberFormType): Promise<void> => {
    try {
      const memberId = member?.id;
      if (!memberId) {
        await createNewInvitation(values);
      } else {
        await updateCompanyMember(memberId, values);
      }
      // Re-bind react-hook-form defaults so the next mount of the modal — typically
      // the admin opening it again to invite a second person — starts from a clean
      // form instead of the previous email + company selection lingering.
      reset(makeDefaultFormValues(member));
      setIsOpen(false);
    } catch (e) {
      // Distinguish business-logic conflicts (already a member, in another org, has a
      // pending invite, etc.) from genuine bugs. Conflicts surface the backend's
      // specific copy in the form; everything else falls back to the generic toast
      // and pages Sentry. Without this, every "you can't invite this person" lands
      // in #eng-alerts as "Operation failed with one or more errors."
      let invitationErrors: OrganizationInvitationError[] = [];
      try {
        invitationErrors = getExtendedErrors(e, OrganizationInvitationErrorSchema);
      } catch {
        // Not an invitation-coded error — leave invitationErrors empty.
      }
      if (invitationErrors.length > 0) {
        setErrorMessage(invitationErrors[0].description);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
        Sentry.captureException(e);
      }
    }
  };

  const onClickCancel = (): void => {
    setIsOpen(false);
    setErrorMessage(undefined);
    reset();
  };

  const [scope, companyIds] = watch(['scope', 'companyIds']);
  const isAdmin = scope === OrganizationMemberItemScope.ADMIN;

  useEffect(() => {
    if (!isAdmin && member?.companies) {
      setValue(
        'companyIds',
        member.companies.map((company) => company.id),
        { shouldValidate: true },
      );
    }
    if (isAdmin) {
      setValue('companyIds', [], { shouldValidate: true });
    }
    //isOpen added as a dependency to ensure the ui reflects company ids on the initial opening of the modal
  }, [isAdmin, setValue, isOpen, member?.companies]);

  useEffect(() => {
    if (!isOpen) {
      setErrorMessage(undefined);
    }
  }, [isOpen]);

  const handleCompanySelect = (companyIds: string[]): void => {
    setValue('companyIds', companyIds, { shouldValidate: true });
  };

  const selectAll = isAdmin;
  const tableDisabled = isAdmin;

  const loading = addOrganizationInvitationLoading || replaceUserAccessInOrganizationLoading;

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          copy={
            <StyledTypography as="span" fontSize="sm">
              {isNewMember
                ? 'They will receive an email inviting them to create an account, and will be automatically attached to your company.'
                : "Update the team member's role and access in your company."}
            </StyledTypography>
          }
          icon={<StyledMembersIcon />}
          title={isNewMember ? 'Invite New Team Member' : 'Update Team Member'}
        />
        <Stack fullWidth style={{ borderTop: `1px solid ${colors.gray[200]}` }}>
          <Stack direction="column">
            <ControlledInput
              control={control}
              name="email"
              rules={{ required: true, validate: validateEmail }}
              id={emailInputId}
              label="Email Address"
              placeholder="Email Address"
              horizontal
              border={false}
              disabled={!isNewMember}
            />

            <ControlledRadioGroup
              control={control}
              name="scope"
              rules={{ required: true }}
              id={roleInputId}
              label="Role"
              horizontal
              options={SCOPE_OPTIONS}
            />

            {isNewMember ? (
              <ControlledRadioGroup
                control={control}
                name="expirationDays"
                rules={{ required: true }}
                id="invitation-expiration-days"
                label="Invitation expires in"
                horizontal
                options={EXPIRATION_OPTIONS}
              />
            ) : null}
          </Stack>
          <MemberTableContainer direction="column" paddingY={6} paddingX={6}>
            <MemberScopeTable
              selectedCompanyIds={companyIds}
              onChangeSelectedCompanyIds={handleCompanySelect}
              disabled={tableDisabled}
              selectAll={selectAll}
            />
          </MemberTableContainer>
        </Stack>

        <Stack direction="column" space={2}>
          <ModalActions>
            <Button disabled={loading} onClick={onClickCancel} rounded="regular" size="base" variant="white">
              Cancel
            </Button>
            <Button
              disabled={!formState.isValid || loading}
              rounded="regular"
              size="base"
              type="submit"
              variant="primary"
              id={inviteNewMemberId}
            >
              {buttonTitle(isNewMember, loading)}
            </Button>
          </ModalActions>
          <GeneralFormError centered error={!!errorMessage}>
            {errorMessage}
          </GeneralFormError>
        </Stack>
      </form>
    </Modal>
  );
};
